import DataTable from "../../components/common/DataTable";
import React from "react";
import { Progress } from "@mantine/core";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { notifications } from "@mantine/notifications";
import { AnalyticsPageLayout, StatCard, ChartCard, TOOLTIP_STYLE, chartProps } from "./AnalyticsLayout";
import { Settings } from "lucide-react";

const serverStatus = [
  { key: "1", server: "API Server 1", status: "healthy", uptime: 99.99, cpu: 45, memory: 62, requests: 125430, avgLatency: 145 },
  { key: "2", server: "API Server 2", status: "healthy", uptime: 99.98, cpu: 52, memory: 68, requests: 118920, avgLatency: 152 },
  { key: "3", server: "Database Server", status: "healthy", uptime: 99.97, cpu: 38, memory: 78, requests: 89340, avgLatency: 128 },
  { key: "4", server: "Cache Server", status: "healthy", uptime: 99.99, cpu: 25, memory: 45, requests: 245600, avgLatency: 8 },
];

const cacheHitRates = [
  { cache: "Redis Main", hitRate: 94.2, requests: 856000 },
  { cache: "CDN Cache", hitRate: 87.5, requests: 1240000 },
  { cache: "Session Cache", hitRate: 98.1, requests: 456000 },
  { cache: "Query Cache", hitRate: 91.3, requests: 678000 },
];

const serverColumns = [
  { title: "Server Name", dataIndex: "server", key: "server" },
  { title: "Status", dataIndex: "status", key: "status", render: (s) => <span className="text-green-500">{s.toUpperCase()}</span> },
  { title: "Uptime", dataIndex: "uptime", key: "uptime", render: (t) => <Progress value={t} color="green" size="sm" style={{ width: 60 }} /> },
  { title: "CPU", dataIndex: "cpu", key: "cpu", render: (t) => <Progress value={t} color={t > 70 ? "yellow" : "blue"} size="sm" style={{ width: 80 }} /> },
  { title: "Memory", dataIndex: "memory", key: "memory", render: (t) => <Progress value={t} color={t > 80 ? "red" : "blue"} size="sm" style={{ width: 80 }} /> },
  { title: "Requests", dataIndex: "requests", key: "requests", render: (t) => t.toLocaleString() },
  { title: "Avg Latency", dataIndex: "avgLatency", key: "avgLatency", render: (t) => `${t}ms` },
];

const serverPerformanceTrend = [
  { hour: "12:00", cpu: 42, memory: 58, requests: 125000 },
  { hour: "1:00", cpu: 48, memory: 62, requests: 142000 },
  { hour: "2:00", cpu: 55, memory: 68, requests: 165000 },
  { hour: "3:00", cpu: 51, memory: 65, requests: 152000 },
  { hour: "4:00", cpu: 45, memory: 62, requests: 138000 },
];

const deploymentStatus = [
  { status: "Successful", value: 245, color: "#10b981" },
  { status: "In Progress", value: 2, color: "#3b82f6" },
  { status: "Failed", value: 3, color: "#ef4444" },
  { status: "Rolled Back", value: 1, color: "#f59e0b" },
];

const storageUsage = [
  { storage: "Primary DB", used: 68 },
  { storage: "Backup Storage", used: 45 },
  { storage: "Cache Storage", used: 32 },
  { storage: "Log Storage", used: 28 },
];

const OperationalAnalyticsPage = () => {
  const handleExport = (format) => {
    notifications.show({ message: `Exporting as ${format.toUpperCase()}...`, color: "green" });
  };

  return (
    <AnalyticsPageLayout
      icon={Settings}
      title="Operational Analytics"
      subtitle="Server performance, infrastructure & deployment metrics"
      onExport={handleExport}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="System Uptime" value={99.98} suffix="%" />
        <StatCard title="Avg Response Time" value={133} suffix=" ms" />
        <StatCard title="Deployments" value={251} suffix="" />
        <StatCard title="Active Servers" value={4} suffix="" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="Server Performance Trend">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={serverPerformanceTrend}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="hour" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="cpu" stroke="#f59e0b" strokeWidth={2} name="CPU (%)" />
                <Line type="monotone" dataKey="memory" stroke="#3b82f6" strokeWidth={2} name="Memory (%)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
        <ChartCard title="Deployment Status Distribution">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={deploymentStatus} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ status, value }) => `${status}: ${value}`} labelLine={false}>
                  {deploymentStatus.map((e, i) => <Cell key={i} fill={e.color} stroke="#0a0a0a" strokeWidth={2} />)}
                </Pie>
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n, p) => [v, p.payload.status]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="mb-8">
      <ChartCard title="Storage Usage">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {storageUsage.map((s, i) => (
            <div key={i} className="exec-stat-card p-4 rounded-lg text-center">
              <div className="text-zinc-400 text-sm font-medium mb-3">{s.storage}</div>
              <Progress value={s.used} color={s.used > 80 ? "red" : "green"} size="lg" />
              <div className="text-zinc-400 text-xs mt-2">{s.used}% used</div>
            </div>
          ))}
        </div>
      </ChartCard>
      </div>

      <div className="mb-8">
      <ChartCard title="Cache Performance">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cacheHitRates.map((c, i) => (
            <div key={i} className="exec-stat-card p-4 rounded-lg text-center">
              <div className="text-zinc-400 text-xs mb-2">{c.cache}</div>
              <Progress value={Math.round(c.hitRate)} color="blue" size="lg" />
              <div className="text-zinc-400 text-xs mt-2">{c.requests.toLocaleString()} requests</div>
            </div>
          ))}
        </div>
      </ChartCard>
      </div>

      <ChartCard title="Server Status & Performance">
        <DataTable dataSource={serverStatus} columns={serverColumns} pagination={false} scroll={{ x: 1200 }} rowKey="key" />
      </ChartCard>
    </AnalyticsPageLayout>
  );
};

export default OperationalAnalyticsPage;
